<template>
	<div class="food-info-popup">
		<ScrollFade>
			<template #scrollContent>
				<div class="food-info-content">
					<div class="food-info">
						<div>{{ translate("ui.food_info_1") }}</div>
						<div class="food-info-text-bold">
							{{ translate("ui.food_info_2") }}
						</div>
						<BidirectionalTextLabel
							:text="translate(`${FrameName.GATHERERS_HUT}.name`)"
							:theme="LabelThemes.RED"
							class="food-info-label"
						/>

						<div class="food-info-icons">
							<div
								:class="`icon-${getTileIconName(FrameName.GATHERERS_HUT)}-80`"
							/>
							<div class="arrow arrow--right-22" />
							<div
								:class="`icon-${getTileIconName(FrameName.GRASSLAND)}-60`"
							/>
						</div>

						<div class="food-info-text-small">
							{{ translate("ui.food_info_3") }}
						</div>
						<BidirectionalTextLabel
							:text="translate(`${FrameName.WHEAT_FARM}.name`)"
							:theme="LabelThemes.RED"
							class="food-info-label food-info-label--second"
						/>
						<div class="food-info-text-level">
							{{
								translate("ui.level_x").replace(
									"{number}",
									requiredLevel.toString(),
								)
							}}
						</div>
						<div class="food-info-icons">
							<div
								:class="`icon-${getTileIconName(FrameName.WHEAT_FARM)}-80`"
							/>
							<div class="arrow arrow--right-22" />
							<div
								:class="`icon-${getTileIconName(FrameName.GRASSLAND)}-60`"
							/>
							<div class="arrow arrow--right-22" />
							<div
								:class="`icon-${getTileIconName(FrameName.WHEAT_FIELD)}-60`"
							/>
						</div>

						<div class="food-info-text-small">
							{{
								translate("ui.food_info_4").replace(
									"{number}",
									gameTickerIntervalInSeconds.toString(),
								)
							}}
						</div>
					</div>
					<div class="food-info-footer">
						<div>{{ translate("ui.food_info_5") }}</div>
						<div class="food-info-text-bold">
							{{ translate("ui.food_info_6") }}
						</div>
					</div>
				</div>
			</template>
		</ScrollFade>
	</div>
</template>

<script lang="ts" setup>
import ScrollFade from "@/components/ScrollFade.vue";
import { GAME_TICKER_INTERVAL } from "@/config/constants";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import useTileRepository from "@/hooks/useTileRepository";
import { LabelThemes } from "@/types/ui";
import { getTileIconName } from "@/utils/tileIconName.ts";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";
import BidirectionalTextLabel from "./BidirectionalTextLabel.vue";

const gameTickerIntervalInSeconds = GAME_TICKER_INTERVAL / 1_000;

const tilerepo = markRaw(useTileRepository());

const requiredLevel = computed((): number => {
	const entity = tilerepo.createEntityByFrameName(FrameName.WHEAT_FARM);
	return entity?.requirements.has(ResourceType.LEVEL)
		? (entity?.requirements.get(ResourceType.LEVEL) ?? 0)
		: 0;
});
</script>

<style scoped lang="scss">
.food-info-popup {
	position: relative;
	@include flex-col-center;
	width: 100%;
	overflow: hidden;
	text-align: center;
	:deep(.bidirectional-text-label) {
		filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
	}
	.food-info {
		padding: 1rem 2rem 0 2rem;
		width: 100%;
		@include flex-col-center;
		gap: 0.5rem;
		&-content {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			align-items: center;
		}
		&-label {
			height: 2rem;
			&--second {
				margin-top: 0.75rem;
			}
		}
		&-text {
			&-level {
				position: relative;
				top: -0.25rem;
				font-weight: bold;
				font-size: 0.688rem;
			}
			&-bold {
				font-weight: bold;
			}
			&-small {
				font-size: 0.813rem;
			}
		}
		&-footer {
			border-top: 0.125rem solid #ede3b9;
			width: 100%;
			font-size: 0.813rem;
			@include flex-col-center;
			position: relative;
			gap: 0.5rem;
			margin-top: 1rem;
			background-image: linear-gradient(
				to top,
				rgb(37, 38, 42),
				rgb(66, 67, 74)
			);
			padding: 1rem 1.5rem;
			color: #f2dcb5;

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				mix-blend-mode: color-burn;
				@include background(
					url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
					150%,
					right top,
					repeat
				);
				width: 100%;
				height: 100%;
				content: "";
			}
		}
		&-icons {
			@include flex-row-center;
			gap: 0.35rem;
			.arrow {
				top: 0.25rem;
			}
		}
	}
}
</style>
